/**
 * Atomic-ish create-property mutation for the V2 wizard.
 *
 * Steps:
 *   1. INSERT property
 *   2. (if rep_override or master rent etc.) UPDATE property fields
 *   3. INSERT each room
 *
 * Done as separate calls because Supabase doesn't expose multi-table
 * transactions through PostgREST. If step 3 partially fails, we keep
 * the property + the rooms that did land — the wizard surfaces the
 * error and the user can retry the failed rooms via legacy admin.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { WizardState } from "@/components/admin/v2/wizard/wizardContext";
import {
  bedroomsOf,
  checklistToRows,
} from "@/components/admin/v2/wizard/checklist";
import { parseOptionalNumber, parseRequiredNumber } from "@/lib/v2-numeric";
import { bilingualToTranslationsJsonb } from "@/lib/v2-translations";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import { operatorStamped } from "@/lib/operator-stamped";
import { copyChecklistPhotosToGallery } from "@/lib/checklist-gallery";

export type CreatePropertyResult = {
  propertyId: number;
  apartmentsCreated: number;
  roomsCreated: number;
  roomsFailed: number;
  errors: string[];
};

export function useCreatePropertyV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (state: WizardState): Promise<CreatePropertyResult> => {
      const errors: string[] = [];

      // Phase 1 — insert property core. Owner/lessor still lives per apartment
      // (COL-252), but the property row now also carries the Step 1 revenue
      // model/collection mode chosen there — the default every apartment in
      // this property seeds from (and can still override individually).
      // address/city/lat/lng are NOT NULL in the legacy schema. lat/lng now
      // come from the address autocomplete's own geocoding response; they fall
      // back to 0/0 only when the admin typed a street instead of picking a
      // suggestion. The public room map treats 0/0 as "no coordinates" and
      // hides itself (hasPlottableCoordinates), so an unpicked address costs a
      // missing map rather than a pin in the Atlantic.
      const propertyImages = state.property.images.filter(Boolean);
      const propertyTranslations = bilingualToTranslationsJsonb({
        en: state.property.description,
        de: state.property.descriptionDe,
      });
      const propMasterRent = parseOptionalNumber(state.property.masterRent);
      const propFeePct = parseOptionalNumber(state.property.coliviqFeePct);
      const propFeeAmount = parseOptionalNumber(
        state.property.coliviqFeeAmount
      );
      // Same inputs, but defaulted to 0 rather than null when left blank —
      // used only for the legacy revenue_pct/revenue_flat_fee mirror below,
      // which properties_revenue_params_check requires to be non-null
      // whenever revenue_model needs a fee (parseOptionalNumber alone would
      // return null for a blank field and fail that check).
      const propFeePctOrZero = parseRequiredNumber(
        state.property.coliviqFeePct,
        0
      );
      const propFeeAmountOrZero = parseRequiredNumber(
        state.property.coliviqFeeAmount,
        0
      );

      // Validate the active model's own fee field before it ever reaches the
      // DB. parseOptionalNumber already rejects non-numeric/negative input
      // (ok: false), but a garbage value for the field the chosen model
      // doesn't use is harmless (it's never written) — so only the field the
      // active revenue_model actually reads gets checked here. Left blank is
      // still fine either way (parseOptionalNumber returns ok: true,
      // value: null for that). A percentage over 100 parses as valid but
      // would fail properties_revenue_params_check's 0-100 bound on the
      // mirrored legacy revenue_pct column with an opaque constraint error —
      // catch it here with a message that names the actual problem.
      if (state.property.revenueModel === "pct_of_rent") {
        if (!propFeePct.ok) {
          throw new Error(
            `Revenue model fee (%): "${state.property.coliviqFeePct}" is not a valid number.`
          );
        }
        if (propFeePct.value != null && propFeePct.value > 100) {
          throw new Error(`Revenue model fee (%) cannot exceed 100.`);
        }
      } else if (state.property.revenueModel === "flat_fee_per_room") {
        if (!propFeeAmount.ok) {
          throw new Error(
            `Revenue model flat fee: "${state.property.coliviqFeeAmount}" is not a valid number.`
          );
        }
      } else if (!propMasterRent.ok) {
        throw new Error(
          `Master rent: "${state.property.masterRent}" is not a valid number.`
        );
      }

      const propertyInsert = operatorStamped<"properties">({
        title: state.property.title.trim(),
        address: state.property.address.trim(),
        // Structured street columns — the Wohnungsgeberbestätigung and the
        // contract read these, never the `address` one-liner.
        street_name: state.property.street_name.trim() || null,
        street_number: state.property.street_number.trim() || null,
        city: state.property.city.trim(),
        zip_code: state.property.zip_code.trim() || null,
        lat: state.property.lat ?? 0,
        lng: state.property.lng ?? 0,
        description: propertyTranslations.en ?? null,
        description_translations: propertyTranslations,
        type: state.property.type || null,
        image_url: propertyImages[0] ?? null,
        images: propertyImages,
        virtual_tour_url:
          normalizeVirtualTourUrl(state.property.virtualTourUrl) || null,
        collection_mode: state.property.collectionMode,
        revenue_model: state.property.revenueModel,
        master_rent:
          state.property.revenueModel === "full_rent" && propMasterRent.ok
            ? propMasterRent.value
            : null,
        coliviq_fee_pct:
          state.property.revenueModel === "pct_of_rent" && propFeePct.ok
            ? propFeePct.value
            : null,
        coliviq_fee_amount:
          state.property.revenueModel === "flat_fee_per_room" &&
          propFeeAmount.ok
            ? propFeeAmount.value
            : null,
        // properties_revenue_params_check (still live on prod — the migration
        // meant to drop it in favor of coliviq_fee_pct/coliviq_fee_amount
        // never made it out of migrations_archive/) requires the legacy
        // revenue_pct/revenue_flat_fee columns to move in lockstep with
        // revenue_model, or the insert is rejected outright. Mirror them
        // here so this new property-level write actually lands; see
        // supabase/migrations_archive/20260524130000_drop_legacy_revenue_params_check.sql.
        // Unlike coliviq_fee_pct/coliviq_fee_amount (which stay null to
        // accurately reflect "not entered yet"), these fall back to 0 when
        // the model requires a fee but the admin hasn't typed one — the
        // constraint demands NOT NULL here, and 0 is in its valid range, so
        // an incomplete-but-otherwise-valid Step 1 form doesn't fail the
        // whole property insert.
        revenue_pct:
          state.property.revenueModel === "pct_of_rent" && propFeePctOrZero.ok
            ? propFeePctOrZero.value
            : null,
        revenue_flat_fee:
          state.property.revenueModel === "flat_fee_per_room" &&
          propFeeAmountOrZero.ok
            ? propFeeAmountOrZero.value
            : null,
      });

      const { data: insertedProperty, error: pErr } = await supabase
        .from("properties")
        .insert(propertyInsert)
        .select("id")
        .single();
      if (pErr) {
        throw new Error(`Could not create property: ${pErr.message}`);
      }
      const propertyId = insertedProperty.id as number;

      // COL-529 slice 1: uploader stamped on the checklist photo files below.
      // Best-effort — uploaded_by is nullable, so a missing user isn't fatal.
      const uploadedBy = (await supabase.auth.getUser()).data.user?.id ?? null;

      // Phase 2 — apartments, each with its own owner + revenue + nested
      // rooms (COL-252). Rooms are created linked to their apartment
      // (rooms.apartment_id). One insert at a time so a partial failure
      // leaves the rest intact.
      let apartmentsCreated = 0;
      let roomsCreated = 0;
      let roomsFailed = 0;
      // Exterior + common-area checklist photos to attribute to the public
      // property gallery once every apartment is processed (COL-529).
      const galleryPhotoPaths: string[] = [];

      for (let i = 0; i < state.apartments.length; i++) {
        const apt = state.apartments[i];
        const aptSize = parseOptionalNumber(apt.size_sqm);
        const aptMasterRent = parseOptionalNumber(apt.masterRent);
        const aptFeePct = parseOptionalNumber(apt.coliviqFeePct);
        const aptFeeAmount = parseOptionalNumber(apt.coliviqFeeAmount);
        const aptNumRooms = parseOptionalNumber(apt.numRooms);
        const aptInsert = operatorStamped<"apartments">({
          property_id: propertyId,
          label: apt.label.trim() || `Apartment ${i + 1}`,
          floor: apt.floor.trim() || null,
          size_sqm: aptSize.ok ? aptSize.value : null,
          // Declared count drives fee-per-room totals; fall back to the number
          // of room rows actually added (COL-252).
          num_rooms:
            aptNumRooms.ok && aptNumRooms.value != null
              ? aptNumRooms.value
              : apt.rooms.length || null,
          owner_user_id: apt.ownerUserId,
          lessor_id: apt.lessorId,
          // Representor override — null (inherit the owner's default) unless the
          // wizard filled a NAME. The note only annotates a name; alone it would
          // store a row that no reader ever surfaces. Same rule as
          // ApartmentEditSheet.
          rep_override: apt.repName.trim()
            ? { name: apt.repName.trim(), note: apt.repNote.trim() || null }
            : null,
          collection_mode: apt.collectionMode,
          revenue_model: apt.revenueModel,
          master_rent:
            apt.revenueModel === "full_rent" && aptMasterRent.ok
              ? aptMasterRent.value
              : null,
          coliviq_fee_pct:
            apt.revenueModel === "pct_of_rent" && aptFeePct.ok
              ? aptFeePct.value
              : null,
          coliviq_fee_amount:
            apt.revenueModel === "flat_fee_per_room" && aptFeeAmount.ok
              ? aptFeeAmount.value
              : null,
          sort_order: i,
        });
        const { data: insertedApt, error: aErr } = await supabase
          .from("apartments")
          .insert(aptInsert)
          .select("id")
          .single();
        if (aErr || !insertedApt) {
          errors.push(
            `Apartment "${aptInsert.label}": ${aErr?.message ?? "insert failed"}`
          );
          // Skip this apartment's rooms — they have nowhere to attach.
          roomsFailed += apt.rooms.length;
          continue;
        }
        apartmentsCreated += 1;
        const apartmentId = insertedApt.id as number;

        // Persist the Step-3 go-live checklist: one apartment_checklist_items
        // row per item_key. bedrooms mirrors the num_rooms used above so the
        // photos proof is scored exactly as the wizard scored it. Plain insert
        // — this table has no operator_id (not operator-scoped). A failure here
        // keeps the apartment + its rooms; the checklist can be re-filled in the
        // apartment view, so it only surfaces an error.
        // ponytail: apartments.doorbell_name is still not written (not captured
        // yet). WLAN password is never persisted; checklistToRows stores only the
        // network name + a "provided" marker. COL-529 slice 1 DOES now persist
        // the uploaded room/exterior photos to apartment_checklist_files below.
        const checklistRows = checklistToRows(apt.checklist, {
          bedrooms: bedroomsOf(apt.numRooms),
          keynestAvailable: state.property.keynestAvailable,
        }).map((row) => ({ ...row, apartment_id: apartmentId }));
        const { data: insertedItems, error: clErr } = await supabase
          .from("apartment_checklist_items")
          .insert(checklistRows)
          .select("id, item_key");
        if (clErr) {
          errors.push(
            `Apartment "${aptInsert.label}" checklist: ${clErr.message}`
          );
        }

        // COL-529: link the uploaded checklist photos (one apartment_checklist_files
        // row per file) to their checklist item — the room/exterior shots to the
        // "photos" item, the inventory/washer proof shots to their own. Non-fatal:
        // the files already live in storage; a failure here only loses the DB link.
        const photosItemId = insertedItems?.find(
          (r) => r.item_key === "photos"
        )?.id;
        const photoFiles = Object.entries(apt.checklist.picPaths ?? {});
        // The building-exterior and common-area shots are listing-worthy; hold
        // on to them to copy into the public gallery after the loop. Per-bedroom
        // (r1..rN) and inventory/washer shots stay checklist-only for now.
        for (const [slot, path] of photoFiles) {
          const shot = slot.split(":")[0];
          if (path && (shot === "outside" || shot === "common"))
            galleryPhotoPaths.push(path);
        }
        // Inventory / washing-machine each carry at most one proof photo, linked
        // to their own checklist item rather than the "photos" one.
        const equipFiles = (["ware", "washer"] as const).flatMap((which) => {
          const path = apt.checklist.equip?.[which]?.photoPath;
          const itemId = insertedItems?.find((r) => r.item_key === which)?.id;
          return path && itemId
            ? [{ itemId, path, name: `${which}:photo` }]
            : [];
        });
        const fileRows = [
          ...photoFiles.map(([slot, path]) => ({
            checklist_item_id: photosItemId,
            file_url: path,
            file_name: slot,
            uploaded_by: uploadedBy,
          })),
          ...equipFiles.map((f) => ({
            checklist_item_id: f.itemId,
            file_url: f.path,
            file_name: f.name,
            uploaded_by: uploadedBy,
          })),
        ].filter((r) => r.checklist_item_id);
        if (fileRows.length > 0) {
          const { error: fErr } = await supabase
            .from("apartment_checklist_files")
            .insert(fileRows);
          if (fErr) {
            errors.push(
              `Apartment "${aptInsert.label}" photos: ${fErr.message}`
            );
          }
        }

        for (let ri = 0; ri < apt.rooms.length; ri++) {
          const r = apt.rooms[ri];
          if (!r.title.trim()) {
            roomsFailed += 1;
            errors.push(`Room with empty title was skipped`);
            continue;
          }
          const price = parseRequiredNumber(r.price, 0);
          if (!price.ok) {
            roomsFailed += 1;
            errors.push(
              `Room "${r.title}": price "${price.raw}" is not a valid number`
            );
            continue;
          }
          const sizeSqm = parseOptionalNumber(r.size_sqm);
          if (!sizeSqm.ok) {
            roomsFailed += 1;
            errors.push(
              `Room "${r.title}": size "${sizeSqm.raw}" is not a valid number`
            );
            continue;
          }
          // COL-529 (Neel): the per-bedroom checklist photos are this room's
          // photos. Map by position — room `ri` ↔ checklist bedroom slot
          // `r{ri+1}`, the same order the wizard keeps rooms and bedroom photo
          // blocks in — copy them into the public gallery and append to the
          // room's own Step-2 images. Best-effort, same as the property gallery.
          const roomShot = `r${ri + 1}`;
          const roomCheckPaths = Object.entries(apt.checklist.picPaths ?? {})
            .filter(([slot, path]) => !!path && slot.split(":")[0] === roomShot)
            .map(([, path]) => path);
          const copiedRoomUrls =
            roomCheckPaths.length > 0
              ? await copyChecklistPhotosToGallery(roomCheckPaths, errors)
              : [];
          const roomImages = [...r.images.filter(Boolean), ...copiedRoomUrls];
          const roomTranslations = bilingualToTranslationsJsonb({
            en: r.descriptionEn,
            de: r.descriptionDe,
          });
          const roomInsert = operatorStamped<"rooms">({
            property_id: propertyId,
            apartment_id: apartmentId,
            title: r.title.trim(),
            price: price.value,
            available: true,
            size_sqm: sizeSqm.value,
            floor: r.floor.trim() || null,
            status: "vacant",
            keynest_pickup_code: r.keynest_pickup_code.trim() || null,
            description: roomTranslations.en ?? null,
            description_translations: roomTranslations,
            images: roomImages,
          });
          const { error: rErr } = await supabase
            .from("rooms")
            .insert(roomInsert);
          if (rErr) {
            roomsFailed += 1;
            errors.push(`Room "${r.title}": ${rErr.message}`);
          } else {
            roomsCreated += 1;
          }
        }
      }

      // COL-529 (Neel): attribute the exterior/common checklist photos to the
      // property's public gallery, appended after any Step-1 gallery images.
      if (galleryPhotoPaths.length > 0) {
        const copied = await copyChecklistPhotosToGallery(
          galleryPhotoPaths,
          errors
        );
        if (copied.length > 0) {
          const nextImages = [...propertyImages, ...copied];
          const { error: gErr } = await supabase
            .from("properties")
            .update({
              images: nextImages,
              image_url: propertyImages[0] ?? copied[0],
            })
            .eq("id", propertyId);
          if (gErr) errors.push(`Property gallery photos: ${gErr.message}`);
        }
      }

      return {
        propertyId,
        apartmentsCreated,
        roomsCreated,
        roomsFailed,
        errors,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}
